if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      this.cancelLoad();
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      if (this.modalContent) this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    resetOpener(opener) {
      opener?.removeAttribute('aria-disabled');
      opener?.classList.remove('loading');
      opener?.querySelector('.loading-overlay__spinner, .loading__spinner')?.classList.add('hidden');
    }

    cancelLoad() {
      this.loadSequence = (this.loadSequence || 0) + 1;
      this.loadController?.abort();
      clearTimeout(this.loadTimer);
      this.resetOpener(this.loadingOpener);
      this.loadingOpener = null;
      this.loadController = null;
    }

    disconnectedCallback() {
      this.cancelLoad();
    }

    async show(opener) {
      this.cancelLoad();
      const sequence = this.loadSequence;
      this.loadController = new AbortController();
      this.loadingOpener = opener;
      this.loadTimer = setTimeout(() => this.loadController?.abort(), 15000);
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner, .loading__spinner')?.classList.remove('hidden');
      let productUrl;
      try {
          const url = new URL(opener.getAttribute('data-product-url'), window.location.origin);
          if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) throw new Error('Invalid product URL');
          productUrl = url.href;
          const response = await fetch(productUrl, { signal: this.loadController.signal });
          if (!response.ok) throw new Error('Product request failed');
          const responseText = await response.text();
          if (sequence !== this.loadSequence) return;
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          if (!this.modalContent || !this.productElement?.dataset.section || !this.productElement.querySelector('product-form')) {
            throw new Error('Product form is unavailable');
          }
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.fixQuantityBreaks();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          super.show(opener);
      } catch (_error) {
          // Ignore a cancelled/obsolete modal. Show a safe product link on a real failure.
          if (sequence !== this.loadSequence || !this.modalContent) return;
          this.modalContent.innerHTML = '';
          const message = document.createElement('p');
          message.setAttribute('role', 'alert');
          message.textContent = window.cartStrings?.error || 'Unable to load purchase options. Please open the product page.';
          this.modalContent.append(message);
          if (productUrl) {
            const link = document.createElement('a');
            link.href = productUrl;
            link.className = 'button';
            link.textContent = 'View product';
            this.modalContent.append(link);
          }
          super.show(opener);
      } finally {
          if (sequence === this.loadSequence) {
            clearTimeout(this.loadTimer);
            this.resetOpener(opener);
            this.loadingOpener = null;
            this.loadController = null;
          }
      }
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      this.modalContent.querySelectorAll('product-info, variant-radios, variant-selects').forEach((element) => {
        element.setAttribute('data-update-url', 'false');
      });
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();

      const stickyAtc = this.productElement.querySelector('sticky-atc');
      if (stickyAtc) stickyAtc.remove();

      const modalDialog = this.productElement.querySelectorAll('modal-dialog');
      if (modalDialog) modalDialog.forEach(modal => modal.remove());
    }

    fixQuantityBreaks() {
      const quantityBreaks = this.productElement.querySelector('quantity-breaks');
      if (!quantityBreaks) return;
      quantityBreaks.querySelectorAll('input').forEach(input => {
        input.id += '-QuickAdd'
      })
      quantityBreaks.querySelectorAll('label').forEach((label, index) => {
        const oldValue = label.getAttribute('for');
        label.setAttribute('for', `${oldValue}-QuickAdd`);
      })
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
        element.dataset.originalSection = sectionId;
        // Set the owner flag before insertion/connectedCallback, not just on
        // the picker after rendering. Quick-add must never navigate its page.
        element.dataset.updateUrl = 'false';
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      if (!product) return;
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';
      
      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}
